import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText,
    Grid, Typography, IconButton, Divider, Box, Tabs, Tab, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTranslation } from 'react-i18next';
import achievementsService from '../services/AchievementsService';
import statsService from '../services/StatisticsService';

const CATEGORY_KEYS = ['all', 'general', 'ronda', 'baciga', 'especiales'];

const TIER_STYLES = {
    bronze: {
        medal: '🥉',
        gradient: 'linear-gradient(135deg, #cd7f32, #a0522d)',
        glow: 'rgba(205,127,50,0.6)',
        border: '#cd7f32',
        bar: '#cd7f32',
    },
    silver: {
        medal: '🥈',
        gradient: 'linear-gradient(135deg, #c0c0c0, #a8a8a8)',
        glow: 'rgba(192,192,192,0.5)',
        border: '#c0c0c0',
        bar: '#c0c0c0',
    },
    gold: {
        medal: '🥇',
        gradient: 'linear-gradient(135deg, #FFD700, #FFA500)',
        glow: 'rgba(255,215,0,0.6)',
        border: '#FFD700',
        bar: '#FFD700',
    },
    platinum: {
        medal: '💎',
        gradient: 'linear-gradient(135deg, #b9f2ff, #87ceeb, #e0f7ff)',
        glow: 'rgba(185,242,255,0.7)',
        border: '#b9f2ff',
        bar: '#b9f2ff',
    },
};

const MedalCard = ({ achievement, stats }) => {
    const { t } = useTranslation();
    const tier = TIER_STYLES[achievement.tier] || TIER_STYLES.bronze;
    const isUnlocked = achievement.unlocked;

    const progressValue = achievement.progressTarget && achievement.progressValue
        ? achievement.progressValue(stats)
        : null;
    const progressPercent = progressValue !== null && achievement.progressTarget
        ? Math.min(100, Math.round((progressValue / achievement.progressTarget) * 100))
        : null;

    const unlockedDate = achievement.unlockedAt
        ? new Date(achievement.unlockedAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
        : null;

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 3,
                background: isUnlocked ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.3)',
                border: `1px solid ${isUnlocked ? tier.border + '60' : 'rgba(255,255,255,0.08)'}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                position: 'relative',
                filter: isUnlocked ? 'none' : 'grayscale(100%)',
                opacity: isUnlocked ? 1 : 0.45,
                boxShadow: isUnlocked ? `0 0 18px ${tier.glow}` : 'none',
                transition: 'all 0.2s ease',
            }}
        >
            {/* Tier badge top-right */}
            <Box sx={{ position: 'absolute', top: 6, right: 8, fontSize: '0.9rem', lineHeight: 1 }}>
                {tier.medal}
            </Box>

            {/* Icon circle */}
            <Box
                sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: isUnlocked ? tier.gradient : 'rgba(80,80,80,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                    filter: isUnlocked ? `drop-shadow(0 0 10px ${tier.glow})` : 'none',
                    position: 'relative',
                }}
            >
                {achievement.icon}
                {!isUnlocked && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            fontSize: '1rem',
                            background: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        🔒
                    </Box>
                )}
            </Box>

            {/* Name */}
            <Typography
                variant="caption"
                fontWeight="bold"
                textAlign="center"
                sx={{ color: isUnlocked ? '#FFD700' : 'rgba(255,255,255,0.5)', lineHeight: 1.2, fontSize: '0.7rem' }}
            >
                {t(achievement.nameKey)}
            </Typography>

            {/* Description */}
            <Typography
                variant="caption"
                textAlign="center"
                sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.2, fontSize: '0.62rem' }}
            >
                {t(achievement.descKey)}
            </Typography>

            {/* Progress bar for countable achievements */}
            {progressPercent !== null && !isUnlocked && (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': { backgroundColor: tier.bar },
                        }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.58rem' }}>
                        {progressValue} / {achievement.progressTarget}
                    </Typography>
                </Box>
            )}

            {/* Unlock date */}
            {isUnlocked && unlockedDate && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem' }}>
                    {unlockedDate}
                </Typography>
            )}
        </Box>
    );
};

const AchievementsModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [achievements, setAchievements] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (open) {
            const currentStats = statsService.getStats();
            // Retroactive silent check: unlock achievements already met before the system existed
            achievementsService.checkAchievements(currentStats, true);
            setStats(currentStats);
            setAchievements(achievementsService.getAchievements());
        }
    }, [open]);

    const category = CATEGORY_KEYS[activeTab];
    const filtered = category === 'all'
        ? achievements
        : achievements.filter((a) => a.category === category);

    const unlockedCount = achievements.filter((a) => a.unlocked).length;
    const total = achievements.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 20,
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: '2rem' }} />
                    <Box>
                        <Typography variant="h5" component="span" fontWeight="bold">
                            {t('achievements.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#FFD700', display: 'block', lineHeight: 1 }}>
                            {t('achievements.counter', { unlocked: unlockedCount, total })}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />

            {/* Category tabs */}
            <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', px: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', minWidth: 70 },
                        '& .Mui-selected': { color: '#FFD700' },
                        '& .MuiTabs-indicator': { backgroundColor: '#FFD700' },
                    }}
                >
                    {CATEGORY_KEYS.map((key) => (
                        <Tab key={key} label={t(`achievements.categories.${key}`)} />
                    ))}
                </Tabs>
            </Box>

            <DialogContent>
                {stats && (
                    <Grid container spacing={1.5}>
                        {filtered.map((achievement) => (
                            <Grid item xs={6} sm={4} md={3} key={achievement.id}>
                                <MedalCard achievement={achievement} stats={stats} />
                            </Grid>
                        ))}
                        {filtered.length === 0 && (
                            <Grid item xs={12}>
                                <DialogContentText sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mt: 4 }}>
                                    {t('achievements.empty_category')}
                                </DialogContentText>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AchievementsModal;
